import React, { useRef } from 'react'
import _ from 'lodash';
import { useBidderDataGetSaleBidderDataQuery } from '@sales/data/bidders/BidderDataQueries';
import AgTechGrid from '@agtech/web/components/Grids/AgTechGrid';
import { isLotInvoiced } from '@sales/data/lots/LotData';
import AgTechGridLink from '@agtech/web/components/Grids/Controls/AgTechGridLink';
import BidderActionMenu from '../menus/BidderActionMenu';
import BidderRegistrationDialog from '../../dialogs/BidderRegistrationDialog';
import { SaleContactGridLink } from '../../../contacts/components/buttons/SaleContactButtons';
import { isSaleBidderTheSame } from '@sales/data/bidders/BidderData';
import { useLotDataGetSaleLotDataQuery } from '@sales/data/lots/LotDataQueries';
import { useAgTechQueryResultCombination } from '@agtech/core/data/queries/AgTechDataQueries';
import { getContactFullName } from '@agtech/data/contacts/ContactData';
import { useBidderDataExportRequest } from '@sales/data/reports/ReportDataActions';
import { useCurrentSale } from '@sales/data/sales/SaleDataStore';
import { AgTechButton, useClickable } from '@agtech/web/components/Buttons/AgTechButtons';
import { AgTechVerticalPopUpMenu, AgTechVerticalPopUpMenuSection } from '@agtech/web/components/Menus/AgTechVerticalPopUpMenu';

const BidderExportButton = () => {
    const currentSale = useCurrentSale();
    const bidderExportRequest = useBidderDataExportRequest();
    const chevronRef = useRef(null);
    const chevronClickable = useClickable(chevronRef);

    return (
        <button
            ref={chevronRef}
            className='button button-standard mr-2'
            onClick={e => {
                e.stopPropagation();
                chevronClickable.renderPopup(() => (
                    <AgTechVerticalPopUpMenu>
                        <AgTechVerticalPopUpMenuSection header='Export As'>
                            <AgTechButton
                                text='Excel'
                                icon='fas fa-file-excel'
                                action={async e => await e.actions.executeWithVisual(
                                    async () => { await bidderExportRequest.execute({ data: { saleId: currentSale.id, format: 'Excel' } }); },
                                    { message: 'Exporting bidders...', delay: 1000 }
                                )}
                            />
                            <AgTechButton
                                text='CSV'
                                icon='fas fa-file-csv'
                                action={async e => await e.actions.executeWithVisual(
                                    async () => { await bidderExportRequest.execute({ data: { saleId: currentSale.id, format: 'Csv' } }); },
                                    { message: 'Exporting bidders...', delay: 1000 }
                                )}
                            />
                        </AgTechVerticalPopUpMenuSection>
                    </AgTechVerticalPopUpMenu>
                ));
            }}
        >
            <i className='fas fa-file-export mr-2' style={{ pointerEvents: 'none' }} />
            Export
            <i className='fas fa-chevron-down text-xs ml-2' style={{ pointerEvents: 'none' }} />
        </button>
    );
}

const BidderRegisteredBiddersGrid = () => {
    const bidderGridDataQuery = useBidderGridDataQuery();

    return (
        <AgTechGrid
            header={{
                title: 'Registered Bidders',
                content: () => (
                    <>
                        <BidderExportButton />
                        <BidderRegistrationDialog.Button icon='fas fa-plus' text='Register new bidder' />
                    </>
                )
            }}
            data={{
                itemQuery: bidderGridDataQuery
            }}
            columns={[
                {
                    header: 'Bidder Number',
                    value: bidder => bidder.bidderNumber,
                    classes: 'centered bg-slate-100 emphasized',
                    width: '130px'
                },
                {
                    header: 'Name',
                    value: bidder => bidder.contact ? (
                        <SaleContactGridLink
                            contactId={bidder.contact.id}
                            contactName={getContactFullName(bidder.contact)}
                        />
                    ) : 'N/A'
                },
                {
                    header: 'Lots Purchased',
                    value: bidder => {
                        const lotsPurchasedByBidder = bidder.lots
                            .filter(lt => lt.originalBidderId === bidder.id && !isLotInvoiced(lt));

                        return (
                            <AgTechGridLink
                                content={lotsPurchasedByBidder.length.toString()}
                                classes='px-2'
                            />
                        )
                    },
                    width: '120px',
                    classes: 'centered'
                },
                {
                    header: 'Lots Invoiced',
                    value: bidder => {
                        const invoicedLotsForbidder = bidder.lots
                            .filter(lt => lt.originalBidderId === bidder.id && isLotInvoiced(lt));

                        return (
                            <AgTechGridLink
                                content={invoicedLotsForbidder.length.toString()}
                                classes='px-2'
                            />
                        )
                    },
                    width: '120px',
                    classes: 'centered'
                }
            ]}
            actions={{
                rendering: bidder => (
                    <BidderActionMenu.Button
                        bidderId={bidder.id}
                        bidderNumber={bidder.bidderNumber}
                    />
                )
            }}
            isRowEntityTheSame={(prev, curr) => {
                return isSaleBidderTheSame(prev, curr);
            }}
            searchConfig={{
                options: [
                    { field: 'Name', value: bidder => bidder.contact ? getContactFullName(bidder.contact) : ''},
                    { field: 'Bidder Number', value: bidder => bidder.bidderNumber ?? 'N/A'}
                ]
            }}
            sortingConfig={{
                getSortingKey: bidder => bidder.bidderNumber ?? 'N/A'
            }}
        />
    )
}

const useBidderGridDataQuery = () => {
    const lotsQuery = useLotDataGetSaleLotDataQuery();
    const bidderDataQuery = useBidderDataGetSaleBidderDataQuery();

    return useAgTechQueryResultCombination({
        firstQuery: lotsQuery,
        secondQuery: bidderDataQuery,
        projection: (lotData, bidderData) => {
            return {
                items: Object.values(bidderData.bidders).map(bidder => ({
                    ...bidder,
                    lots: Object.values(lotData.lots).filter(lt => bidder.id === lt.originalBidderId)
                })),
                context: {
                    lotData: lotData
                }
            }
        }
    })
}

export default BidderRegisteredBiddersGrid;
